/**
 * SOLID Review
 * S — Strong. What every client decision shares: the envelope it returns, the one way a refused
 *     rule becomes a failure, and the answer of a rule that allows the move.
 * O — Strong. A new decision uses these; none of them changes per decision.
 * L — N/A.
 * I — Strong. Two types, two small functions.
 * D — Strong. Nothing — not even the kernel, which `domain-no-port-kernel` keeps out of decisions/.
 */

/**
 * The client twin of `backend/src/domain/decisions/shared.ts` (frontend strict-ports plan, FE1,
 * 2026-10-03), without the environment, events and outbox: a client decision reads no clock and mints
 * no id, and the client publishes nothing.
 *
 * The failure is `{ code, message }`, structurally the kernel's `IEntityPortError` and the contract's
 * `ErrorPort` — not `shared/Result`'s `Error`, which does not survive `JSON.stringify`. It is declared
 * here because decisions/ may not import the kernel; the contract's guard is what checks the two agree.
 */
interface IEntityRefusal {
  readonly code: string;
  readonly message: string;
}

export type TDecisionResult<T> =
  | { readonly success: true; readonly value: T }
  | { readonly success: false; readonly error: IEntityRefusal };

/**
 * The envelope every client decision returns; `C` is its changes shape, `D` its DTO. Built in one place, the decision
 * builder's `answering` (`ClientDecision`, since frontend-domain-oo P6 — `decided` here until then).
 */
export interface IEntityDecision<C, D> {
  readonly changes: C;
  readonly dto: D;
}

/**
 * A move the rules allow, with what it sends — the answer of a `ClientTable` plan (frontend-domain-oo P3), which a
 * decision turns into its envelope. Its other answer is `refuse`'s.
 */
export function allowed<T>(value: T): TDecisionResult<T> {
  return { success: true, value };
}

/** A rule the table refuses — the move is not sent, and nothing changes. */
export function refuse<T>(message: string, code: string): TDecisionResult<T> {
  const error: IEntityRefusal = { code, message };
  return { success: false, error };
}

/** The refusal codes the table and session decisions use. */
export const REFUSAL = {
  /** The table lacks what the move needs: a room, a round, an identity. Today's handlers returned silently. */
  NOT_READY: 'NOT_READY',
  /** The move is not this player's to make in this phase. */
  NOT_YOUR_MOVE: 'NOT_YOUR_MOVE',
  /** Meyer cannot be believed, only lifted. */
  MEYER_CANNOT_BE_BELIEVED: 'MEYER_CANNOT_BE_BELIEVED',
  /** A resync outside live play: lobby, idle and game over have no round to catch up on. */
  NOT_IN_PLAY: 'NOT_IN_PLAY',
  /** A lobby read after the table left the lobby. */
  NOT_IN_LOBBY: 'NOT_IN_LOBBY',
  /** The session is already doing it: an entry into the game screen while one is in flight (FE2). */
  ALREADY: 'ALREADY',
  /** A name the player typed that cannot be a display name (FE2). */
  INVALID_NAME: 'INVALID_NAME',
  /** A table code no room has (FE2). */
  NOT_FOUND: 'NOT_FOUND',
  /** A form field the request cannot carry: an address that is not one, a code that is not four digits (FE3). */
  INVALID_INPUT: 'INVALID_INPUT',
  /** The server's own refusal, passed on in its words — the Application's `SERVER_REFUSED` code (FE3). */
  SERVER: 'SERVER',
} as const;
